import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, Gift, ChevronLeft, Sparkles, Star, RotateCcw, Cake, Flame, ShoppingBag, PartyPopper, Baby, ToyBrick, Puzzle, Bot, Gamepad2, User, Users } from "lucide-react";

const NAVY = "#1B1F5C";

const STEPS = [
  {
    key: "gender",
    title: "Who is the gift for?",
    sub: "Let's find their perfect surprise",
    options: [
      { value: "Boys", label: "A Boy", icon: User, color: "#CDE6F8" },
      { value: "Girls", label: "A Girl", icon: User, color: "#FBD5E0" },
      { value: "Unisex", label: "Any Child", icon: Users, color: "#DFF3D8" },
    ],
  },
  {
    key: "age",
    title: "How old are they?",
    sub: "Age-perfect picks coming up",
    options: [
      { value: "0 - 1 Years", label: "0 - 1 Years", icon: Baby, color: "#FDE3BF" },
      { value: "1 - 3 Years", label: "1 - 3 Years", icon: ToyBrick, color: "#FBD5E0" },
      { value: "3 - 6 Years", label: "3 - 6 Years", icon: Puzzle, color: "#DFF3D8" },
      { value: "6 - 10 Years", label: "6 - 10 Years", icon: Bot, color: "#CDE6F8" },
      { value: "10+ Years", label: "10+ Years", icon: Gamepad2, color: "#FBE8B8" },
    ],
  },
  {
    key: "occasion",
    title: "What's the occasion?",
    sub: "Every moment deserves the right gift",
    options: [
      { value: "Birthday", label: "Birthday", icon: Cake, color: "#FBD5E0" },
      { value: "Festival", label: "Festival", icon: Flame, color: "#FDE3BF" },
      { value: "Return Gifts", label: "Return Gift", icon: ShoppingBag, color: "#DFF3D8" },
      { value: "Special Occasions", label: "Just Because", icon: PartyPopper, color: "#CDE6F8" },
    ],
  },
];

const Stars = ({ rating }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={12} className={i <= Math.round(rating) ? "text-[#F79433] fill-[#F79433]" : "text-gray-300 fill-gray-300"} />
    ))}
  </span>
);

const GiftFinderQuiz = ({ open, onClose, products, addToCart, toggleWishlist, wishlist }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= STEPS.length;

  const results = useMemo(() => {
    if (!done) return [];
    const scored = products
      .filter((p) => !p.giftCard)
      .map((p) => {
        let score = 0;
        if (p.gender === answers.gender) score += 3;
        else if (p.gender === "Unisex" || answers.gender === "Unisex") score += 2;
        if (p.age === answers.age) score += 3;
        if (p.occasion === answers.occasion) score += 3;
        score += p.rating / 5;
        return { ...p, score };
      })
      .sort((a, b) => b.score - a.score);
    return scored.slice(0, 3);
  }, [done, answers, products]);

  const pick = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setStep((s) => s + 1);
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
  };

  const handleClose = () => {
    onClose();
    setTimeout(restart, 300);
  };

  if (!open) return null;
  const currentStep = STEPS[step];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 md:px-8 py-5 flex items-center justify-between rounded-t-3xl" style={{ backgroundColor: NAVY }}>
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-[#FCD44E] flex items-center justify-center">
              <Gift size={19} style={{ color: NAVY }} />
            </span>
            <div className="text-left">
              <p className="font-display font-extrabold text-white text-lg leading-tight">Gift Finder</p>
              <p className="text-[11px] font-semibold text-white/60">Find the perfect gift in 3 quick steps</p>
            </div>
          </div>
          <button onClick={handleClose} className="h-9 w-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Close gift finder">
            <X size={17} />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 md:px-8 pt-5">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <span
                key={s.key}
                className="h-1.5 flex-1 rounded-full"
                style={{
                  backgroundColor: i < step || done ? "#35C3C1" : i === step ? "#FCD44E" : "#E8EAF3",
                  transition: "background-color 300ms ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        {!done ? (
          <div className="px-6 md:px-8 py-6 text-center">
            <p className="text-[11px] font-extrabold tracking-widest text-[#35C3C1]">STEP {step + 1} OF {STEPS.length}</p>
            <h3 className="mt-1 font-display font-extrabold text-2xl" style={{ color: NAVY }}>{currentStep.title}</h3>
            <p className="text-[12.5px] font-semibold text-gray-500 mt-1">{currentStep.sub}</p>
            <div className={`mt-6 grid gap-3 ${currentStep.options.length > 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-3"}`}>
              {currentStep.options.map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => pick(currentStep.key, opt.value)}
                    className="rounded-2xl border-2 border-gray-100 py-5 px-3 flex flex-col items-center gap-2.5 hover:border-[#35C3C1] hover:shadow-lg hover:-translate-y-0.5 transition-transform duration-200"
                  >
                    <span className="h-12 w-12 rounded-full flex items-center justify-center" style={{ backgroundColor: opt.color }}>
                      <Icon size={21} style={{ color: NAVY }} strokeWidth={1.8} />
                    </span>
                    <span className="text-[12.5px] font-extrabold" style={{ color: NAVY }}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-6 inline-flex items-center gap-1 text-[12px] font-extrabold text-gray-400 hover:text-[#1B1F5C] transition-colors">
                <ChevronLeft size={14} /> Back
              </button>
            )}
          </div>
        ) : (
          <div className="px-6 md:px-8 py-6">
            <div className="text-center">
              <span className="inline-flex h-12 w-12 rounded-full bg-[#FCD44E] items-center justify-center">
                <Sparkles size={22} style={{ color: NAVY }} />
              </span>
              <h3 className="mt-2 font-display font-extrabold text-2xl" style={{ color: NAVY }}>Perfect Gifts Found!</h3>
              <p className="text-[12.5px] font-semibold text-gray-500 mt-1">Handpicked just for your little one</p>
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {results.map((p) => (
                <div key={p.id} className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-left flex flex-col cursor-pointer" onClick={() => { onClose(); navigate(`/product/${p.id}`); }}>
                  <div className="relative bg-[#F7F7FA] p-3">
                    <img src={p.image} alt={p.name} className="h-28 w-full object-contain mix-blend-multiply" />
                  </div>
                  <div className="p-3 flex flex-col flex-1">
                    <p className="text-[12px] font-bold leading-snug flex-1" style={{ color: NAVY }}>{p.name}</p>
                    <div className="mt-1 flex items-center gap-1">
                      <Stars rating={p.rating} />
                      <span className="text-[10.5px] font-semibold text-gray-400">({p.reviews})</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/product/${p.id}`); }}
                      data-testid={`quiz-discover-btn-${p.id}`}
                      className="mt-2.5 w-full h-8 rounded-md text-white text-[11px] font-extrabold hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: NAVY }}
                    >
                      Discover More
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-center gap-4">
              <button onClick={restart} className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full border-2 border-[#35C3C1] text-[11.5px] font-extrabold tracking-wide text-[#1B1F5C] hover:bg-[#35C3C1] hover:text-white transition-colors">
                <RotateCcw size={13} /> START OVER
              </button>
              <button onClick={handleClose} className="px-5 h-10 rounded-full text-white text-[11.5px] font-extrabold tracking-wide hover:opacity-90 transition-opacity" style={{ backgroundColor: NAVY }}>
                CONTINUE SHOPPING
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftFinderQuiz;
